import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageLayout from '../../components/feature/PageLayout';
import ArticleCard from '../../components/feature/ArticleCard';
import { articles, type Block } from '../../mocks/articles';
import { useDocMeta } from '../../hooks/useDocMeta';

function RenderBlock({ block }: { block: Block }) {
  if (block.type === 'h') {
    return (
      <h2 className="mt-8 mb-3 flex items-center gap-3 border-l-4 border-accent-500 pl-3 font-display text-xl font-black text-foreground-900 md:text-2xl">
        {block.text}
      </h2>
    );
  }
  if (block.type === 'q') {
    return (
      <blockquote className="my-6 rounded-r-lg border-l-4 border-primary-500 bg-primary-100/40 px-5 py-4">
        <p className="font-display text-base leading-7 text-primary-900">“{block.text}”</p>
        {block.by && <footer className="mt-2 text-xs text-foreground-500">—— {block.by}</footer>}
      </blockquote>
    );
  }
  if (block.type === 'list') {
    return (
      <ul className="my-4 flex flex-col gap-2.5">
        {block.items.map((it, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm leading-7 text-foreground-800 md:text-base">
            <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500"></span>
            {it}
          </li>
        ))}
      </ul>
    );
  }
  return <p className="my-4 text-sm leading-8 text-foreground-800 md:text-base md:leading-8">{block.text}</p>;
}

export default function ArticleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const article = articles.find((a) => a.id === Number(id)) || articles[0];

  const headings = useMemo(
    () => article.body.filter((b): b is Extract<Block, { type: 'h' }> => b.type === 'h'),
    [article],
  );
  const related = articles.filter((a) => a.id !== article.id && a.category === article.category).slice(0, 3);
  const fallback = articles.filter((a) => a.id !== article.id && a.category !== article.category).slice(0, 3);
  const recs = related.length >= 3 ? related : [...related, ...fallback].slice(0, 3);

  const setMeta = useDocMeta(
    `${article.title}｜故纸瓜档`,
    `${article.excerpt}${article.excerpt.length < 60 ? '（八卦爆料·陈年旧瓜·考古重读）' : ''}`,
  );
  useEffect(() => {
    setMeta();
    window.scrollTo(0, 0);
  }, [id, setMeta]);

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://bdnsv.cn/' },
      { '@type': 'ListItem', position: 2, name: article.category, item: 'https://bdnsv.cn/column/dangnian-zhenxiang' },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://bdnsv.cn/article/${article.id}` },
    ],
  };
  const blogSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.excerpt,
    image: article.cover,
    datePublished: article.date,
    author: { '@type': 'Person', name: article.author },
    publisher: { '@type': 'Organization', name: '故纸瓜档' },
    mainEntityOfPage: `https://bdnsv.cn/article/${article.id}`,
  };

  return (
    <PageLayout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(blogSchema) }} />

      {/* 顶部横幅 */}
      <section className="relative flex min-h-[300px] w-full items-end overflow-hidden md:min-h-[340px]">
        <img
          src={article.cover}
          alt={article.coverAlt}
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/45 to-foreground-950/20"></div>
        <div className="container-x relative z-10 w-full pb-8 pt-24">
          <nav className="flex items-center gap-2 text-xs text-background-50/70" aria-label="面包屑">
            <button onClick={() => navigate('/')} className="cursor-pointer hover:text-accent-300">首页</button>
            <i className="ri-arrow-right-s-line"></i>
            <button onClick={() => navigate('/column/kaogu-chongdu')} className="cursor-pointer hover:text-accent-300">{article.category}</button>
            <i className="ri-arrow-right-s-line"></i>
            <span className="max-w-[180px] truncate text-background-50">{article.title}</span>
          </nav>
        </div>
      </section>

      <section className="bg-background-100">
        <div className="container-x grid grid-cols-1 gap-10 py-10 lg:grid-cols-12">
          {/* 主内容 */}
          <div className="lg:col-span-8">
            <article className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-accent-100 px-3 py-1 text-xs font-medium text-accent-700">{article.category}</span>
                {article.tags.map((t) => (
                  <span key={t} className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">#{t}</span>
                ))}
              </div>

              <h1 className="mt-4 font-display text-2xl font-black leading-snug text-foreground-900 md:text-4xl">
                {article.title}
              </h1>
              <p className="mt-4 border-l-4 border-accent-400 pl-4 text-sm italic leading-7 text-foreground-600 md:text-base">
                {article.excerpt}
              </p>

              <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-y border-background-200 py-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-600 font-display text-sm font-bold text-background-50">
                    {article.author.slice(-2)}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">{article.author}</p>
                    <p className="text-xs text-foreground-500">故纸瓜档 · 考据编辑</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                  <span className="inline-flex items-center gap-1.5"><i className="ri-calendar-line"></i>更新于 {article.date}</span>
                  <span className="inline-flex items-center gap-1.5"><i className="ri-time-line"></i>{article.readTime}</span>
                  <span className="inline-flex items-center gap-1.5"><i className="ri-eye-line"></i>{article.views.toLocaleString()} 阅读</span>
                </div>
              </div>

              <div className="prose prose-neutral mt-2 max-w-none">
                {article.body.map((b, i) => (
                  <RenderBlock key={i} block={b} />
                ))}
              </div>

              {/* 分享 */}
              <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-lg bg-background-100 p-4">
                <span className="inline-flex items-center gap-2 text-sm text-foreground-700">
                  <i className="ri-share-forward-line"></i>觉得这篇值得转给一起考古的朋友？
                </span>
                <div className="flex items-center gap-2">
                  <button className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-background-50 text-foreground-600 hover:bg-primary-600 hover:text-background-50" aria-label="分享到微博"><i className="ri-weibo-line"></i></button>
                  <button className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-background-50 text-foreground-600 hover:bg-primary-600 hover:text-background-50" aria-label="分享到微信"><i className="ri-wechat-line"></i></button>
                  <button className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-background-50 text-foreground-600 hover:bg-primary-600 hover:text-background-50" aria-label="复制链接"><i className="ri-link"></i></button>
                </div>
              </div>
            </article>
          </div>

          {/* 侧栏 */}
          <aside className="lg:col-span-4">
            {/* 目录 */}
            {headings.length > 0 && (
              <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="flex items-center gap-2 font-display text-base font-bold text-foreground-900">
                  <i className="ri-list-unordered text-accent-600"></i>本文目录
                </h3>
                <nav className="mt-4 flex flex-col gap-2.5" aria-label="文章目录">
                  <a
                    href="#"
                    onClick={(e) => e.preventDefault()}
                    className="flex items-start gap-2.5 text-sm leading-6 text-foreground-600 hover:text-primary-700"
                  >
                    <i className="ri-corner-down-right-line mt-1 text-xs"></i>开篇导读
                  </a>
                  {headings.map((h, i) => (
                    <a
                      key={i}
                      href="#"
                      onClick={(e) => e.preventDefault()}
                      className="flex items-start gap-2.5 text-sm leading-6 text-foreground-600 hover:text-primary-700"
                    >
                      <i className="ri-corner-down-right-line mt-1 text-xs"></i>{h.text}
                    </a>
                  ))}
                </nav>
              </div>
            )}

            {/* 推荐 */}
            <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="flex items-center gap-2 font-display text-base font-bold text-foreground-900">
                <i className="ri-bookmark-line text-accent-600"></i>同栏目相关推荐
              </h3>
              <div className="mt-4 flex flex-col gap-4">
                {recs.map((a) => (
                  <button key={a.id} onClick={() => navigate(`/article/${a.id}`)} className="group flex cursor-pointer items-start gap-3 text-left">
                    <img src={a.cover} alt={a.coverAlt} className="h-16 w-24 shrink-0 rounded-md object-cover object-top" />
                    <div className="min-w-0">
                      <p className="line-clamp-2 text-sm font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-700">{a.title}</p>
                      <p className="mt-1 text-xs text-foreground-500">{a.readTime} · {(a.views / 1000).toFixed(1)}k 阅读</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* 更多文章 */}
      <section className="bg-background-50">
        <div className="container-x py-12">
          <div className="flex items-end justify-between">
            <h2 className="font-display text-xl font-black text-foreground-900 md:text-2xl">延伸阅读 · 更多陈年旧瓜</h2>
            <button onClick={() => navigate('/')} className="cursor-pointer text-sm text-primary-700 hover:text-primary-800">返回首页 <i className="ri-arrow-right-line ml-1"></i></button>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {recs.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        </div>
      </section>
    </PageLayout>
  );
}